import { z } from "zod";

/**
 * Platform FieldType contracts (ui-safe).
 * Import: `@repo/ui/lib/field-types/contracts`
 * Also re-exported at `@repo/api/modules/field-types/contracts` for server/web.
 *
 * Do not add Create app, React Server Components, or server-only handlers here.
 */

export const CORE_FIELD_TYPES = [
	"text",
	"textarea",
	"richtext",
	"number",
	"boolean",
	"boolean_group",
	"single_select",
	"multi_select",
	"int_range",
	"number_range",
	"discrete_scale",
	"asset",
	"custom",
	"password",
	"otp",
] as const;

export type CoreFieldType = (typeof CORE_FIELD_TYPES)[number];

export const coreFieldTypeSchema = z.enum(CORE_FIELD_TYPES);

/** Composers are not FieldTypes — listed so consumers never promote them into CORE_FIELD_TYPES. */
export const GATHER_COMPOSERS = [
	"SourcesCollector",
	"QuestionnaireForm",
] as const;

export type GatherComposer = (typeof GATHER_COMPOSERS)[number];

export const gatherComposerSchema = z.enum(GATHER_COMPOSERS);

export const FIELD_PRESENTATIONS = [
	"input",
	"textarea",
	"tiptap",
	"tiles",
	"dropdown",
	"radio",
	"segmented",
	"switch",
	"checkbox",
	"check_menu",
	"dual_slider",
	"discrete_scale",
	"upload_picker",
	"password_input",
	"otp_slots",
] as const;

export type FieldPresentation = (typeof FIELD_PRESENTATIONS)[number];

export const fieldPresentationSchema = z.enum(FIELD_PRESENTATIONS);

export const FIELD_COMMIT_MODES = ["live", "confirm", "immediate"] as const;

export type FieldCommitMode = (typeof FIELD_COMMIT_MODES)[number];

export const fieldCommitModeSchema = z.enum(FIELD_COMMIT_MODES);

/** Textarea / EditableFieldControl atom chrome (distinct from definition `commit`). */
export const FIELD_SAVE_MODES = ["confirm", "debounce", "inline"] as const;

export type FieldSaveMode = (typeof FIELD_SAVE_MODES)[number];

export const fieldSaveModeSchema = z.enum(FIELD_SAVE_MODES);

/** Boolean checkbox row chrome (BooleanField `chrome` prop / fieldDefinition). */
export const FIELD_BOOLEAN_CHROMES = ["plain", "button", "bordered_row"] as const;

export type FieldBooleanChrome = (typeof FIELD_BOOLEAN_CHROMES)[number];

export const fieldBooleanChromeSchema = z.enum(FIELD_BOOLEAN_CHROMES);

export const fieldDefinitionSchema = z.object({
	fieldType: coreFieldTypeSchema,
	presentation: fieldPresentationSchema.optional(),
	commit: fieldCommitModeSchema.optional(),
	/** Textarea chrome; distinct from session `commit`. */
	saveMode: fieldSaveModeSchema.optional(),
	mixed: z.boolean().optional(),
	/** Caller-resolved string; atoms do not i18n. */
	mixedTooltip: z.string().optional(),
	/** BooleanField checkbox chrome; ignored for other fieldTypes until renderer uses it. */
	chrome: fieldBooleanChromeSchema.optional(),
	/**
	 * Definition-level error message (JSON-safe string for gather).
	 * Atom/FieldShell runtime `error` prop is ReactNode (see form parity plan).
	 */
	error: z.string().optional(),
	/** OTP digit count (atom default 6 when omitted). */
	length: z.number().int().min(4).max(12).optional(),
	customSubtype: z.string().min(1).optional(),
});

export type FieldDefinition = z.infer<typeof fieldDefinitionSchema>;

export const gatherSlotFieldBindingSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("field"),
		def: fieldDefinitionSchema,
	}),
	z.object({
		kind: z.literal("composer"),
		composer: gatherComposerSchema,
	}),
]);

export type GatherSlotFieldBinding = z.infer<
	typeof gatherSlotFieldBindingSchema
>;

export function isCoreFieldType(value: unknown): value is CoreFieldType {
	return (
		typeof value === "string" &&
		(CORE_FIELD_TYPES as readonly string[]).includes(value)
	);
}

export function assertNotComposerAsFieldType(value: string): void {
	if ((GATHER_COMPOSERS as readonly string[]).includes(value)) {
		throw new Error(
			`${value} is a gather composer, not a CoreFieldType. Use SourcesCollector / QuestionnaireForm bindings.`,
		);
	}
}
